import {
  ArrowLeft,
  Camera,
  Clapperboard,
  Clock3,
  Download,
  Film,
  ImagePlus,
  Mic,
  MonitorPlay,
  RefreshCcw,
  Sparkles,
  Square,
  UploadCloud,
  Wand2,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardFrame, { type DashboardNavItem } from '../components/DashboardFrame';
import { buyerNavItemsConfig } from '../buyerDashboardConfig';
import { setBuyerRecordedVideoDraft, type VideoFilterPreset } from '../utils/buyerRecordedVideoDraft';
import { addUploadedStudioLibraryItem } from '../utils/uploadedStudioLibrary';
import { mediaRecordingService, type RecordingUploadResult } from '../../../shared/services';
import { useShop } from '../../../shared/store';

type RecorderStatus = 'idle' | 'requesting' | 'recording' | 'ready' | 'uploading' | 'success' | 'error';
type VideoQuality = 'standard' | 'hd' | 'cinema';

const filterPresets: Record<
  VideoFilterPreset,
  { label: string; brightness: number; contrast: number; saturation: number; grayscale: number; sepia: number }
> = {
  natural: { label: 'Natural', brightness: 1, contrast: 1, saturation: 1, grayscale: 0, sepia: 0 },
  cinematic: { label: 'Cinematic', brightness: 0.96, contrast: 1.14, saturation: 1.08, grayscale: 0, sepia: 0.08 },
  mono: { label: 'Black & White', brightness: 1.02, contrast: 1.1, saturation: 0.25, grayscale: 1, sepia: 0 },
  warm: { label: 'Warm Glow', brightness: 1.04, contrast: 1.06, saturation: 1.18, grayscale: 0, sepia: 0.16 },
};

const qualityOptions: Record<VideoQuality, { label: string; width: number; height: number; fps: number }> = {
  standard: { label: '720p / 24 FPS', width: 1280, height: 720, fps: 24 },
  hd: { label: '1080p / 30 FPS', width: 1920, height: 1080, fps: 30 },
  cinema: { label: '1440p / 30 FPS', width: 2560, height: 1440, fps: 30 },
};

const defaultRecorderName = () => `haapa-video-${new Date().toISOString().replace(/[:.]/g, '-')}`;

const sanitizeFileBaseName = (value: string, fallback: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || fallback;

const pickVideoMimeType = () => {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return undefined;
  }

  return ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'].find((candidate) =>
    MediaRecorder.isTypeSupported(candidate),
  );
};

const extensionFromMimeType = (mimeType: string) => {
  if (mimeType.includes('mp4')) {
    return 'mp4';
  }

  return 'webm';
};

const formatDuration = (seconds: number) => {
  const mins = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const secs = Math.floor(seconds % 60)
    .toString()
    .padStart(2, '0');
  return `${mins}:${secs}`;
};

const formatFileSize = (value?: number) => {
  if (!value) {
    return '0 B';
  }

  const units = ['B', 'KB', 'MB', 'GB'];
  const unitIndex = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const normalizedSize = value / 1024 ** unitIndex;
  return `${normalizedSize.toFixed(normalizedSize >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const readFileAsDataUrl = async (file: File | null): Promise<string | undefined> => {
  if (!file) {
    return undefined;
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : undefined);
    reader.onerror = () => reject(reader.error ?? new Error('Unable to read selected image.'));
    reader.readAsDataURL(file);
  });
};

const BuyerVideoRecorderPage = () => {
  const { t } = useTranslation();
  const { currentUser } = useShop();
  const navigate = useNavigate();
  const navItems = useMemo<DashboardNavItem[]>(
    () =>
      buyerNavItemsConfig.map((item) => ({
        ...item,
        label: item.label ?? (item.labelKey ? t(item.labelKey) : item.id),
      })),
    [t],
  );
  const supportsRecording =
    typeof window !== 'undefined' &&
    typeof MediaRecorder !== 'undefined' &&
    typeof HTMLCanvasElement !== 'undefined' &&
    typeof window.navigator.mediaDevices?.getUserMedia === 'function';

  const [status, setStatus] = useState<RecorderStatus>('idle');
  const [feedback, setFeedback] = useState('Record a video clip, adjust the look with simple controls, and send it back into Create Content.');
  const [recordingName, setRecordingName] = useState(defaultRecorderName);
  const [filterPreset, setFilterPreset] = useState<VideoFilterPreset>('natural');
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);
  const [quality, setQuality] = useState<VideoQuality>('hd');
  const [mirrorPreview, setMirrorPreview] = useState(false);
  const [includeMicrophone, setIncludeMicrophone] = useState(true);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [recordedFile, setRecordedFile] = useState<File | null>(null);
  const [posterFile, setPosterFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<RecordingUploadResult | null>(null);
  const [captureSource, setCaptureSource] = useState<'camera' | 'upload'>('camera');

  const liveVideoRef = useRef<HTMLVideoElement | null>(null);
  const previewVideoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const sourceStreamRef = useRef<MediaStream | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const drawFrameRef = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const revokePreviewUrl = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  };

  const setNextPreviewUrl = (nextUrl: string | null) => {
    revokePreviewUrl();
    previewUrlRef.current = nextUrl;
    setPreviewUrl(nextUrl);
  };

  const stopTimer = () => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const stopCanvasLoop = () => {
    if (drawFrameRef.current !== null) {
      window.cancelAnimationFrame(drawFrameRef.current);
      drawFrameRef.current = null;
    }
  };

  const stopSourceStream = () => {
    sourceStreamRef.current?.getTracks().forEach((track) => track.stop());
    sourceStreamRef.current = null;
    if (liveVideoRef.current) {
      liveVideoRef.current.srcObject = null;
    }
  };

  const drawIdlePreview = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');

    if (!canvas || !context) {
      return;
    }

    context.clearRect(0, 0, canvas.width, canvas.height);
    const gradient = context.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#fff7ed');
    gradient.addColorStop(1, '#fed7aa');
    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width, canvas.height);

    context.fillStyle = '#f97316';
    context.font = '700 28px sans-serif';
    context.fillText('Haapa Video Studio', 56, 88);

    context.fillStyle = '#7c2d12';
    context.font = '500 20px sans-serif';
    context.fillText('Camera preview appears here', 56, 126);

    for (let index = 0; index < 12; index += 1) {
      const x = 56 + index * 72;
      const height = 26 + ((Math.sin(index * 0.7) + 1) / 2) * 110;
      context.fillStyle = index % 2 === 0 ? '#fdba74' : '#fb923c';
      context.fillRect(x, canvas.height - height - 52, 42, height);
    }
  };

  const resolveFilterString = () => {
    const preset = filterPresets[filterPreset];
    const brightnessValue = ((brightness / 100) * preset.brightness).toFixed(2);
    const contrastValue = ((contrast / 100) * preset.contrast).toFixed(2);
    const saturationValue = ((saturation / 100) * preset.saturation).toFixed(2);
    return [
      `brightness(${brightnessValue})`,
      `contrast(${contrastValue})`,
      `saturate(${saturationValue})`,
      `grayscale(${preset.grayscale})`,
      `sepia(${preset.sepia})`,
    ].join(' ');
  };

  const startCanvasLoop = () => {
    stopCanvasLoop();

    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    const video = liveVideoRef.current;

    if (!canvas || !context || !video) {
      return;
    }

    const draw = () => {
      if (video.readyState >= 2) {
        context.save();
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.filter = resolveFilterString();

        if (mirrorPreview) {
          context.translate(canvas.width, 0);
          context.scale(-1, 1);
        }

        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        context.restore();

        context.fillStyle = 'rgba(15, 23, 42, 0.12)';
        context.fillRect(0, canvas.height - 88, canvas.width, 88);
        context.fillStyle = '#ffffff';
        context.font = '600 20px sans-serif';
        context.fillText(recordingName || 'Haapa video take', 28, canvas.height - 46);
        context.font = '500 15px sans-serif';
        context.fillText(`${filterPresets[filterPreset].label} • ${qualityOptions[quality].label}`, 28, canvas.height - 22);
      } else {
        drawIdlePreview();
      }

      drawFrameRef.current = window.requestAnimationFrame(draw);
    };

    draw();
  };

  const startTimer = () => {
    stopTimer();
    setElapsedSeconds(0);
    timerRef.current = window.setInterval(() => {
      setElapsedSeconds((current) => current + 1);
    }, 1000);
  };

  const resetTake = (message = 'Recorder reset. Start a fresh video take or choose an existing clip.') => {
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }
    recorderRef.current = null;
    stopTimer();
    stopCanvasLoop();
    stopSourceStream();
    setRecordedFile(null);
    setPosterFile(null);
    setElapsedSeconds(0);
    setNextPreviewUrl(null);
    setUploadResult(null);
    setStatus('idle');
    setFeedback(message);
    drawIdlePreview();
  };

  const createPosterFromCanvas = async () => {
    const canvas = canvasRef.current;

    if (!canvas) {
      return null;
    }

    return new Promise<File | null>((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(null);
            return;
          }

          resolve(
            new File([blob], `${sanitizeFileBaseName(recordingName, 'haapa-video')}-poster.jpg`, {
              type: 'image/jpeg',
            }),
          );
        },
        'image/jpeg',
        0.92,
      );
    });
  };

  const prepareNamedVideoFile = (file: File) => {
    const extension = file.name.split('.').pop() ?? extensionFromMimeType(file.type || 'video/webm');
    return new File([file], `${sanitizeFileBaseName(recordingName, 'haapa-video')}.${extension}`, {
      type: file.type || 'video/webm',
    });
  };

  const handlePickedFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    stopTimer();
    stopCanvasLoop();
    stopSourceStream();
    recorderRef.current = null;
    setRecordedFile(file);
    setRecordingName(file.name.replace(/\.[^.]+$/, '') || defaultRecorderName());
    setStatus('ready');
    setFeedback('Video file added. You can preview it, then send it back into Create Content.');
    setCaptureSource('upload');
    setUploadResult(null);
    setNextPreviewUrl(URL.createObjectURL(file));
    setPosterFile(null);
    drawIdlePreview();
  };

  const handleStartRecording = async () => {
    if (!supportsRecording) {
      setStatus('error');
      setFeedback('This browser does not support in-app camera recording.');
      return;
    }

    resetTake('Requesting camera access...');
    setStatus('requesting');

    try {
      const qualityConfig = qualityOptions[quality];
      const stream = await window.navigator.mediaDevices.getUserMedia({
        audio: includeMicrophone,
        video: {
          facingMode: 'user',
          width: { ideal: qualityConfig.width },
          height: { ideal: qualityConfig.height },
          frameRate: { ideal: qualityConfig.fps, max: qualityConfig.fps },
        },
      });

      sourceStreamRef.current = stream;

      if (liveVideoRef.current) {
        liveVideoRef.current.srcObject = stream;
        await liveVideoRef.current.play().catch(() => undefined);
      }

      startCanvasLoop();

      const canvas = canvasRef.current;
      if (!canvas) {
        throw new Error('Video canvas is not ready.');
      }

      const canvasStream = canvas.captureStream(qualityConfig.fps);
      const composedStream = new MediaStream([
        ...canvasStream.getVideoTracks(),
        ...(includeMicrophone ? stream.getAudioTracks() : []),
      ]);
      const mimeType = pickVideoMimeType();
      const recorder = mimeType ? new MediaRecorder(composedStream, { mimeType }) : new MediaRecorder(composedStream);

      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const finalMimeType = recorder.mimeType || mimeType || 'video/webm';
        const nextFile = new File(
          [new Blob(chunksRef.current, { type: finalMimeType })],
          `${sanitizeFileBaseName(recordingName, 'haapa-video')}.${extensionFromMimeType(finalMimeType)}`,
          { type: finalMimeType },
        );

        setRecordedFile(nextFile);
        setNextPreviewUrl(URL.createObjectURL(nextFile));
        setStatus('ready');
        setFeedback('Video recording ready. Use it in Create Content when you are happy with the result.');
        setCaptureSource('camera');
        setUploadResult(null);
        void createPosterFromCanvas().then((poster) => setPosterFile(poster));
        stopTimer();
        stopCanvasLoop();
        stopSourceStream();
        drawIdlePreview();
      };

      recorder.start(250);
      startTimer();
      setStatus('recording');
      setFeedback('Recording in progress. Your current look adjustments are being applied to the video.');
    } catch (error: unknown) {
      stopTimer();
      stopCanvasLoop();
      stopSourceStream();
      setStatus('error');
      setFeedback(error instanceof Error ? error.message : 'Unable to start camera recording.');
      drawIdlePreview();
    }
  };

  const handleStopRecording = () => {
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }
  };

  const handleUseInDraft = () => {
    if (!recordedFile) {
      setStatus('error');
      setFeedback('Record a video or choose a clip first.');
      return;
    }

    setBuyerRecordedVideoDraft({
      videoFile: prepareNamedVideoFile(recordedFile),
      posterFile,
      createdAt: Date.now(),
      source: 'recorder',
      filterPreset,
      brightness,
      contrast,
      saturation,
      mirrorPreview,
      includeMicrophone,
    });
    void navigate('/dashboard/create-content');
  };

  const handlePosterImagePicked = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setPosterFile(file);
    if (file) {
      setFeedback(`Poster image "${file.name}" added. You can upload the video or use it in your draft now.`);
    }
  };

  const handleUpload = async () => {
    if (!recordedFile) {
      setStatus('error');
      setFeedback('Record a video or choose a clip before uploading.');
      return;
    }

    const preparedFile = prepareNamedVideoFile(recordedFile);

    try {
      setStatus('uploading');
      setFeedback('Uploading video to POST /api/media/recordings/video ...');
      const result = await mediaRecordingService.uploadRecording({
        endpoint: 'video',
        file: preparedFile,
        sourceType: captureSource,
        fallbackUserId: currentUser?.id,
        title: recordingName || preparedFile.name,
        previewImageFile: posterFile,
      });
      const imageUrl = await readFileAsDataUrl(posterFile).catch(() => undefined);
      setRecordedFile(preparedFile);
      setUploadResult(result);
      addUploadedStudioLibraryItem({
        id: result.recordingId ? `recording-video-${result.recordingId}` : result.publicId ?? `${preparedFile.name}-${Date.now()}`,
        userId: currentUser?.id,
        title: recordingName || preparedFile.name,
        mediaType: 'video',
        mediaUrl: result.url || previewUrl || '',
        imageUrl: result.previewImageUrl ?? imageUrl ?? '/vite.svg',
        uploadedAt: new Date().toISOString(),
        backendUrl: result.url,
      });
      setStatus('success');
      setFeedback('Video uploaded successfully, saved in the backend database, and added to the public video feed.');
    } catch (error: unknown) {
      setStatus('error');
      setFeedback(error instanceof Error ? error.message : 'Video upload failed.');
    }
  };

  const handleDownload = () => {
    if (!recordedFile) {
      setStatus('error');
      setFeedback('There is no video take to download yet.');
      return;
    }

    const preparedFile = prepareNamedVideoFile(recordedFile);
    const downloadUrl = URL.createObjectURL(preparedFile);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = preparedFile.name;
    document.body.append(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 0);
  };

  useEffect(() => {
    drawIdlePreview();

    return () => {
      if (recorderRef.current?.state === 'recording') {
        recorderRef.current.stop();
      }
      stopTimer();
      stopCanvasLoop();
      stopSourceStream();
      revokePreviewUrl();
    };
  }, []);

  useEffect(() => {
    if (previewVideoRef.current) {
      previewVideoRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed, previewUrl]);

  useEffect(() => {
    if (status === 'recording') {
      startCanvasLoop();
    }
    // startCanvasLoop intentionally reads the latest recorder refs and visual state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brightness, contrast, saturation, filterPreset, mirrorPreview, recordingName, quality, status]);

  const statusTone =
    status === 'recording'
      ? 'bg-red-100 text-red-700'
      : status === 'uploading'
        ? 'bg-amber-100 text-amber-700'
        : status === 'success'
          ? 'bg-emerald-100 text-emerald-700'
      : status === 'ready'
        ? 'bg-emerald-100 text-emerald-700'
        : status === 'error'
          ? 'bg-red-100 text-red-700'
          : 'bg-orange-100 text-orange-700';

  return (
    <DashboardFrame
      brand={t('dashboardBuyer.brand')}
      roleLabel={t('dashboardBuyer.roleLabel')}
      userName={currentUser?.name ?? t('dashboardBuyer.defaultUserName')}
      userImageUrl={currentUser?.profileImageUrl}
      pageTitle="Video Studio"
      pageDescription="Record video in a dedicated camera workspace with simple look controls and send it back into Create Content."
      navItems={navItems}
      activeNavId="create-content"
      headerVariant="buyer"
      showSettings={false}
      headerAction={
        <div className="flex flex-wrap gap-2">
          <Link
            to="/dashboard/create-content"
            className="inline-flex items-center gap-2 rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm font-semibold text-orange-700 transition hover:bg-orange-50"
          >
            <ArrowLeft className="h-4 w-4" />
            Back To Draft
          </Link>
          <Link
            to="/products"
            className="rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600"
          >
            {t('dashboardBuyer.actions.browseBooks')}
          </Link>
        </div>
      }
    >
      <div className="space-y-6">
        <section className="overflow-hidden rounded-[30px] border border-orange-200 bg-[radial-gradient(circle_at_top_left,_rgba(251,146,60,0.22),_transparent_30%),linear-gradient(135deg,_#fff7ed,_#ffffff_55%,_#ffedd5)] p-6 shadow-[0_30px_90px_-60px_rgba(249,115,22,0.75)]">
          <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-orange-600">Haapa Creator Studio</p>
              <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
                Professional video studio with direct backend publishing
              </h1>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
                Capture camera footage, apply a polished visual look, upload it to your video endpoint, and return the same clip to your animation draft with a generated poster image.
              </p>
              <div className="mt-5 flex flex-wrap gap-3 text-xs font-semibold">
                <span className="rounded-full border border-orange-200 bg-white/90 px-3 py-1.5 text-orange-700">Scene filters</span>
                <span className="rounded-full border border-orange-200 bg-white/90 px-3 py-1.5 text-orange-700">Poster frame</span>
                <span className="rounded-full border border-orange-200 bg-white/90 px-3 py-1.5 text-orange-700">API upload</span>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
              <div className="rounded-3xl border border-white/80 bg-white/85 p-4 backdrop-blur">
                <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">Endpoint</p>
                <p className="mt-2 text-sm font-semibold text-slate-900">POST `/api/media/recordings/video`</p>
                <p className="mt-1 text-xs text-slate-500">Maps to `http://localhost:4000/api/media/recordings/video` in local development.</p>
              </div>
              <div className="rounded-3xl border border-white/80 bg-white/85 p-4 backdrop-blur">
                <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">Source</p>
                <p className="mt-2 text-sm font-semibold text-slate-900">{captureSource === 'camera' ? 'Camera capture' : 'Uploaded video file'}</p>
              </div>
              <div className="rounded-3xl border border-white/80 bg-white/85 p-4 backdrop-blur">
                <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">Upload Status</p>
                <p className="mt-2 text-sm font-semibold text-slate-900">{uploadResult?.url ? 'Uploaded' : 'Not uploaded yet'}</p>
              </div>
            </div>
          </div>
        </section>
        {status === 'success' && uploadResult ? (
          <section className="rounded-[26px] border border-emerald-200 bg-emerald-50 px-5 py-4 shadow-[0_20px_45px_-35px_rgba(16,185,129,0.75)]">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">Upload Complete</p>
                <p className="mt-1 text-base font-semibold text-slate-900">Video uploaded successfully.</p>
                <p className="mt-1 text-sm text-slate-600">Your clip is now saved on the backend and is still ready to return to Create Content with its picture.</p>
              </div>
              <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-emerald-700">Success</span>
            </div>
          </section>
        ) : null}
        <section className="grid gap-4 xl:grid-cols-[340px_minmax(0,1fr)]">
          <aside className="space-y-4">
            <article className="rounded-3xl border border-orange-100 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-2xl bg-orange-500 text-white shadow-[0_20px_45px_-28px_rgba(249,115,22,0.85)]">
                  <Camera className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-600">Camera Setup</p>
                  <h2 className="text-lg font-semibold text-slate-900">Recording Controls</h2>
                </div>
              </div>

              <div className="mt-5 space-y-4 text-sm">
                <label className="grid gap-2">
                  <span className="font-semibold text-slate-700">Recording name</span>
                  <input
                    value={recordingName}
                    onChange={(event) => setRecordingName(event.target.value)}
                    className="rounded-2xl border border-orange-200 px-4 py-3 text-slate-800 focus:border-orange-400 focus:outline-none"
                  />
                </label>

                <label className="grid gap-2">
                  <span className="font-semibold text-slate-700">Quality</span>
                  <select
                    value={quality}
                    onChange={(event) => setQuality(event.target.value as VideoQuality)}
                    className="rounded-2xl border border-orange-200 bg-white px-4 py-3 text-slate-800 focus:border-orange-400 focus:outline-none"
                  >
                    {Object.entries(qualityOptions).map(([value, option]) => (
                      <option key={value} value={value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="rounded-2xl border border-orange-100 bg-orange-50/70 p-4">
                  <div className="flex items-center gap-2 text-slate-800">
                    <Mic className="h-4 w-4 text-orange-600" />
                    <span className="font-semibold">Capture options</span>
                  </div>
                  <div className="mt-3 space-y-3">
                    <label className="flex items-center justify-between gap-3">
                      <span className="text-slate-600">Include microphone</span>
                      <input type="checkbox" checked={includeMicrophone} onChange={(event) => setIncludeMicrophone(event.target.checked)} className="h-4 w-4 accent-orange-500" />
                    </label>
                    <label className="flex items-center justify-between gap-3">
                      <span className="text-slate-600">Mirror preview</span>
                      <input type="checkbox" checked={mirrorPreview} onChange={(event) => setMirrorPreview(event.target.checked)} className="h-4 w-4 accent-orange-500" />
                    </label>
                  </div>
                </div>
              </div>
            </article>

            <article className="rounded-3xl border border-orange-100 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-2">
                <Wand2 className="h-4 w-4 text-orange-600" />
                <h3 className="text-base font-semibold text-slate-900">Basic video look</h3>
              </div>
              <div className="mt-4 space-y-4 text-sm">
                <label className="grid gap-2">
                  <span className="font-semibold text-slate-700">Filter style</span>
                  <select
                    value={filterPreset}
                    onChange={(event) => setFilterPreset(event.target.value as VideoFilterPreset)}
                    className="rounded-2xl border border-orange-200 bg-white px-4 py-3 text-slate-800 focus:border-orange-400 focus:outline-none"
                  >
                    {Object.entries(filterPresets).map(([value, option]) => (
                      <option key={value} value={value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="grid gap-2">
                  <span className="flex items-center justify-between font-semibold text-slate-700">
                    <span>Brightness</span>
                    <span className="text-xs text-slate-500">{brightness}%</span>
                  </span>
                  <input type="range" min="80" max="130" value={brightness} onChange={(event) => setBrightness(Number(event.target.value))} className="accent-orange-500" />
                </label>

                <label className="grid gap-2">
                  <span className="flex items-center justify-between font-semibold text-slate-700">
                    <span>Contrast</span>
                    <span className="text-xs text-slate-500">{contrast}%</span>
                  </span>
                  <input type="range" min="80" max="140" value={contrast} onChange={(event) => setContrast(Number(event.target.value))} className="accent-orange-500" />
                </label>

                <label className="grid gap-2">
                  <span className="flex items-center justify-between font-semibold text-slate-700">
                    <span>Saturation</span>
                    <span className="text-xs text-slate-500">{saturation}%</span>
                  </span>
                  <input type="range" min="0" max="150" value={saturation} onChange={(event) => setSaturation(Number(event.target.value))} className="accent-orange-500" />
                </label>
              </div>
            </article>
          </aside>

          <div className="space-y-4">
            <article className="overflow-hidden rounded-[28px] border border-orange-100 bg-white shadow-[0_30px_80px_-50px_rgba(15,23,42,0.35)]">
              <div className="border-b border-orange-100 bg-gradient-to-r from-orange-50 via-white to-orange-50 px-6 py-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-600">Video Timeline Studio</p>
                    <h2 className="mt-1 text-2xl font-semibold text-slate-900">Record video with simple scene styling</h2>
                    <p className="mt-2 max-w-3xl text-sm text-slate-600">{feedback}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusTone}`}>{status}</span>
                    <span className="inline-flex items-center gap-2 rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-700">
                      <Clock3 className="h-3.5 w-3.5" />
                      {formatDuration(elapsedSeconds)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-5 p-6">
                <div className="rounded-[26px] border border-orange-100 bg-gradient-to-br from-white via-orange-50/70 to-orange-100/70 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <Sparkles className="h-4 w-4 text-orange-600" />
                      <span>{filterPresets[filterPreset].label} look • {qualityOptions[quality].label}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Film className="h-4 w-4 text-orange-500" />
                      <span>{includeMicrophone ? 'Video + voice' : 'Video only'}</span>
                    </div>
                  </div>

                  <div className="mt-4 overflow-hidden rounded-[22px] border border-orange-100 bg-slate-950">
                    <canvas ref={canvasRef} width={1280} height={720} className="aspect-video h-auto w-full bg-slate-950" />
                    <video ref={liveVideoRef} playsInline muted className="hidden" />
                  </div>
                </div>

                <div className="grid gap-3 rounded-[26px] border border-orange-100 bg-orange-50/50 p-4 lg:grid-cols-[repeat(5,minmax(0,1fr))]">
                  {['Intro scene', 'Warm cut', 'Focus shot', 'Main moment', 'Ending frame'].map((label, index) => (
                    <div key={label} className="rounded-2xl border border-orange-200 bg-white p-3">
                      <div className="aspect-video rounded-xl bg-gradient-to-br from-slate-900 via-slate-700 to-slate-900" />
                      <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-orange-600">Clip {index + 1}</p>
                      <p className="mt-1 text-sm text-slate-700">{label}</p>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={handleStartRecording}
                    disabled={status === 'recording'}
                    className="inline-flex items-center gap-2 rounded-2xl bg-orange-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Camera className="h-4 w-4" />
                    Start Recording
                  </button>
                  <button
                    type="button"
                    onClick={handleStopRecording}
                    disabled={status !== 'recording'}
                    className="inline-flex items-center gap-2 rounded-2xl border border-orange-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Square className="h-4 w-4" />
                    Stop
                  </button>
                  <button
                    type="button"
                    onClick={() => resetTake()}
                    className="inline-flex items-center gap-2 rounded-2xl border border-orange-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-orange-50"
                  >
                    <RefreshCcw className="h-4 w-4" />
                    Reset
                  </button>
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-2xl border border-orange-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-orange-50">
                    <UploadCloud className="h-4 w-4" />
                    Choose Video
                    <input type="file" accept="video/*,.mp4,.webm,.ogg,.mov,.avi,.mkv" onChange={handlePickedFile} className="hidden" />
                  </label>
                </div>

                {previewUrl ? (
                  <div className="rounded-3xl border border-orange-100 bg-orange-50/50 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="text-sm font-semibold text-slate-900">Preview video</p>
                      <label className="flex items-center gap-3 text-sm text-slate-600">
                        <span>Playback speed</span>
                        <input type="range" min="0.75" max="1.5" step="0.05" value={playbackSpeed} onChange={(event) => setPlaybackSpeed(Number(event.target.value))} className="accent-orange-500" />
                      </label>
                    </div>
                    <video ref={previewVideoRef} controls playsInline src={previewUrl} className="mt-3 aspect-video w-full rounded-2xl border border-orange-100 bg-slate-950" />
                  </div>
                ) : null}
              </div>
            </article>

            <div className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
              <article className="rounded-3xl border border-orange-100 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-2">
                  <MonitorPlay className="h-4 w-4 text-orange-600" />
                  <h3 className="text-base font-semibold text-slate-900">Current take</h3>
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-orange-100 bg-orange-50/60 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">Video file</p>
                    <p className="mt-2 text-sm font-medium text-slate-800">{recordedFile?.name ?? 'No video selected yet'}</p>
                  </div>
                  <div className="rounded-2xl border border-orange-100 bg-orange-50/60 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">Size</p>
                    <p className="mt-2 text-sm font-medium text-slate-800">{formatFileSize(recordedFile?.size)}</p>
                  </div>
                  <div className="rounded-2xl border border-orange-100 bg-orange-50/60 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">Poster image</p>
                    <p className="mt-2 text-sm font-medium text-slate-800">{posterFile?.name ?? 'Generated after recording'}</p>
                  </div>
                  <div className="rounded-2xl border border-orange-100 bg-orange-50/60 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">Filter</p>
                    <p className="mt-2 text-sm font-medium text-slate-800">{filterPresets[filterPreset].label}</p>
                  </div>
                </div>
                {!supportsRecording ? (
                  <p className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    This browser does not expose the camera APIs needed for in-app recording. You can still choose an existing video file.
                  </p>
                ) : null}
              </article>

              <article className="rounded-3xl border border-orange-100 bg-white p-5 shadow-sm">
                <h3 className="text-base font-semibold text-slate-900">Publish And Export</h3>
                <p className="mt-2 text-sm text-slate-600">
                  Upload the clip to your video endpoint first, or send it directly into your animation draft with a generated picture.
                </p>
                <div className="mt-5 space-y-3">
                  <label className="flex cursor-pointer items-center justify-center gap-2 rounded-2xl border border-orange-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-orange-50">
                    <ImagePlus className="h-4 w-4" />
                    {posterFile ? 'Change Picture' : 'Add Picture'}
                    <input type="file" accept="image/*" onChange={handlePosterImagePicked} className="hidden" />
                  </label>
                  <button
                    type="button"
                    onClick={handleUpload}
                    disabled={!recordedFile || status === 'recording' || status === 'uploading'}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <UploadCloud className="h-4 w-4" />
                    {status === 'uploading' ? 'Uploading Video...' : 'Upload To Video API'}
                  </button>
                  <button
                    type="button"
                    onClick={handleUseInDraft}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-orange-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-orange-600"
                  >
                    <Clapperboard className="h-4 w-4" />
                    Use In Create Content
                  </button>
                  <button
                    type="button"
                    onClick={handleDownload}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-orange-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-orange-50"
                  >
                    <Download className="h-4 w-4" />
                    Download Video
                  </button>
                  <div className="rounded-2xl border border-orange-100 bg-orange-50/70 p-4 text-xs text-slate-600">
                    <div className="flex items-center gap-2 font-semibold text-slate-800">
                      <ImagePlus className="h-4 w-4 text-orange-600" />
                      Auto draft fill
                    </div>
                    <p className="mt-2">
                      The recorder returns a full video, copies it into the trailer slot, and adds a generated poster image so your animation draft is immediately usable.
                    </p>
                  </div>
                  {uploadResult ? (
                    <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-xs text-slate-700">
                      <p><span className="font-semibold text-slate-900">Uploaded file:</span> {uploadResult.originalName || recordedFile?.name}</p>
                      <p className="mt-1"><span className="font-semibold text-slate-900">Endpoint:</span> POST /api/media/recordings/video</p>
                      <p className="mt-1"><span className="font-semibold text-slate-900">Size:</span> {formatFileSize(uploadResult.size ?? recordedFile?.size)}</p>
                      {uploadResult.url ? <a href={uploadResult.url} target="_blank" rel="noreferrer" className="mt-2 block break-all font-medium text-emerald-700 underline">{uploadResult.url}</a> : null}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-orange-100 bg-orange-50/70 p-4 text-xs text-slate-600">
                      <p className="font-semibold text-slate-900">Backend request</p>
                      <p className="mt-2 font-mono text-[11px] text-orange-700">curl -X POST http://localhost:4000/api/media/recordings/video</p>
                    </div>
                  )}
                </div>
              </article>
            </div>
          </div>
        </section>
      </div>
    </DashboardFrame>
  );
};

export default BuyerVideoRecorderPage;
